/**
 * Core-100 R4 — CORE-BC GAP-004 (HARD-003): "A shop cannot see its own automation posture." One place that states
 * which Core automations exist, whether each is on for this shop, what each may do, how each is reversed and where
 * its actions are recorded — read from `automation_posture(shop)`, which reads every gate from the record that owns
 * it. An OBSERVER: the gates are changed where they live; this panel changes nothing. A failed read is said, never
 * shown as "nothing automated".
 */
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

export interface AutomationPostureRow {
  automation_key: string;
  name: string;
  owning_capability: string;
  state: 'on' | 'off';
  configurable: boolean;
  setting: string;
  what_it_does: string;
  how_to_reverse: string;
  recorded_in: string;
}

export function summarizePosture(rows: AutomationPostureRow[]): string {
  const on = rows.filter((r) => r.state === 'on').length;
  return `${on} of ${rows.length} automations are on for this shop.`;
}

export function AutomationPosturePanel({ shopId }: { shopId: string | null | undefined }) {
  const q = useQuery({
    queryKey: ['automation-posture', shopId],
    enabled: !!shopId,
    queryFn: async (): Promise<AutomationPostureRow[]> => {
      const { data, error } = await supabase.rpc('automation_posture' as never, { p_shop_id: shopId } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as AutomationPostureRow[];
    },
  });

  return (
    <Card data-testid="automation-posture">
      <CardHeader>
        <CardTitle className="text-base">What OptiBay does on its own here</CardTitle>
        <CardDescription>
          Every automation, whether it is on for this shop, what it may do, how to undo it and where what it did is
          recorded. Everything else waits for a person.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {q.isError && (
          <p className="text-muted-foreground" data-testid="automation-posture-error">
            The automation settings could not be read, so nothing is claimed about them.
          </p>
        )}
        {q.data && <p className="text-muted-foreground" data-testid="automation-posture-summary">{summarizePosture(q.data)}</p>}
        {q.data?.map((r) => (
          <div key={r.automation_key} className="border-t pt-2" data-testid={`automation-${r.automation_key}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium">{r.name}</span>
              <Badge variant={r.state === 'on' ? 'default' : 'outline'} data-testid={`automation-state-${r.automation_key}`}>
                {r.state === 'on' ? 'On' : 'Off'}
              </Badge>
              <span className="text-xs text-muted-foreground">{r.owning_capability}</span>
            </div>
            <p>{r.what_it_does}</p>
            <p className="text-xs text-muted-foreground">Undo: {r.how_to_reverse}</p>
            <p className="text-xs text-muted-foreground">Recorded in: <code>{r.recorded_in}</code></p>
            <p className="text-xs text-muted-foreground">{r.configurable ? r.setting : `Not a shop setting — ${r.setting}`}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
